import { get } from 'lodash'

import assert from './assert'
import { TrackingPlanType } from 'containers/Protocols/types'

export const sourceSchemaPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug'])
  const path = `/${args.workspaceSlug}/sources/${args.sourceSlug}/schema`

  if (!args.eventType && args.eventName) {
    throw new Error(
      `eventType must be provided for provided eventName: ${args.eventName}`
    )
  }

  if (args.eventType && !args.eventName) {
    return `${path}/${args.eventType}`
  }

  if (args.eventType && args.eventName) {
    return `${path}/${args.eventType}/${args.eventName}`
  }

  return path
}

export const sourceSchemaEventsPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug'])
  return sourceSchemaPath({
    ...args,
    eventType: 'events',
    eventName: args.eventId
  })
}

export const sourceSchemaIdentifyPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug'])
  return sourceSchemaPath({ ...args, eventType: 'identify' })
}

export const sourceSchemaGroupPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug'])
  return sourceSchemaPath({ ...args, eventType: 'group' })
}

export const violationSummariesPerSourcePath = args => {
  assert(args, ['workspaceSlug'])
  const queryParams = args.timeframe ? `?timeframe=${args.timeframe}` : ''
  return `/${args.workspaceSlug}/protocols/violations${queryParams}`
}

export const violationSummariesPerEventPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug'])
  const queryParams = args.timeframe ? `?timeframe=${args.timeframe}` : '' // optional timeframe filter
  const optionalTypeTab = args.type ? `/${args.type}` : '' // /track, /identify, etc. -- defaults to /track
  return `/${args.workspaceSlug}/protocols/violations/${args.sourceSlug}${optionalTypeTab}${queryParams}`
}

export const dataValidationViolationSummariesPerViolationPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug', 'trackingPlanId', 'type'])
  const queryParams = args.timeframe ? `?timeframe=${args.timeframe}` : '' // optional timeframe filter
  const name = ['identify', 'group'].includes(args.type) ? 'view' : args.name // event name (or `view` for identify.group)
  return `/${args.workspaceSlug}/protocols/data-validation/${args.trackingPlanId}/sources/${args.sourceSlug}/${args.type}/${name}${queryParams}`
}

export const violationSummariesPerViolationPath = args => {
  assert(args, ['workspaceSlug', 'sourceSlug', 'type'])
  const queryParams = args.timeframe ? `?timeframe=${args.timeframe}` : '' // optional timeframe filter
  const name = ['identify', 'group'].includes(args.type) ? 'view' : args.name // event name (or `view` for identify.group)
  return `/${args.workspaceSlug}/protocols/violations/${args.sourceSlug}/${args.type}/${name}${queryParams}`
}

export const transformationsPath = args => {
  assert(args, ['workspaceSlug'])
  return `/${args.workspaceSlug}/protocols/transformations`
}

export const transformationsCreatePath = args => {
  assert(args, ['workspaceSlug'])
  return `/${args.workspaceSlug}/protocols/transformations/create`
}

export const transformationsEditPath = args => {
  assert(args, ['workspaceSlug', 'transformationId'])
  return `/${args.workspaceSlug}/protocols/transformations/${args.transformationId}/edit`
}

// In order not to update all of the trackingPlanPath calls, instead of checking to see if the tracking plan is not a live tracking plan, we explicitly check to see if it is a type of library
const isLibrary = tpType =>
  tpType === TrackingPlanType.PropertyLibrary ||
  tpType === TrackingPlanType.RuleLibrary

export const trackingPlanPath = args => {
  assert(args, ['workspaceSlug'])
  const urlParam = args.resourceId ? `/${args.resourceId}` : ''
  const eventNameQuery = get(args, 'query.name')
  const queryParams = eventNameQuery ? `?name=${eventNameQuery}` : ''
  let trackingPlanType = '/tracking-plans'
  if (isLibrary(args.trackingPlanType)) {
    trackingPlanType = '/libraries'
  }
  return `/${args.workspaceSlug}/protocols${trackingPlanType}${urlParam}${queryParams}`
}

export const trackingPlanLiveEditorPath = args => {
  assert(args, ['workspaceSlug'])
  const urlParam = args.resourceId ? `/${args.resourceId}` : ''
  const eventNameQuery = get(args, 'query.name')
  const queryParams = eventNameQuery ? `?name=${eventNameQuery}` : ''
  let trackingPlanType = '/tracking-plans'
  if (isLibrary(args.trackingPlanType)) {
    trackingPlanType = '/libraries'
  }
  return `/${args.workspaceSlug}/protocols${trackingPlanType}${urlParam}${queryParams}/draft`
}

export const trackingPlanMergeChangesPath = args => {
  assert(args, ['workspaceSlug'])
  const urlParam = args.resourceId ? `/${args.resourceId}` : ''
  const eventNameQuery = get(args, 'query.name')
  const queryParams = eventNameQuery ? `?name=${eventNameQuery}` : ''
  let trackingPlanType = '/tracking-plans'
  if (isLibrary(args.trackingPlanType)) {
    trackingPlanType = '/libraries'
  }
  return `/${args.workspaceSlug}/protocols${trackingPlanType}${urlParam}${queryParams}/merge`
}

export const trackingPlanChangeLogPath = args => {
  assert(args, ['workspaceSlug'])
  const urlParam = args.resourceId ? `/${args.resourceId}` : ''
  const eventNameQuery = get(args, 'query.name')
  const queryParams = eventNameQuery ? `?name=${eventNameQuery}` : ''
  const editPath = 'draft/'
  const parentPath = args.isReadOnly ? '' : editPath
  let trackingPlanType = '/tracking-plans'
  if (isLibrary(args.trackingPlanType)) {
    trackingPlanType = '/libraries'
  }
  return `/${args.workspaceSlug}/protocols${trackingPlanType}${urlParam}/${parentPath}change-log${queryParams}`
}

export const trackingPlanDataValidationPath = args => {
  assert(args, ['workspaceSlug', 'trackingPlanId'])
  return `/${args.workspaceSlug}/protocols/data-validation/${args.trackingPlanId}`
}

export const trackingPlanConnectedSourcesSidesheetPath = args => {
  assert(args, ['workspaceSlug', 'trackingPlanId'])
  return `/${args.workspaceSlug}/protocols/tracking-plans/${args.trackingPlanId}/sources`
}

export const trackingPlanLibraryConnectionsSidesheetPath = args => {
  assert(args, ['workspaceSlug', 'trackingPlanId', 'trackingPlanType'])
  const { isFromMergeView, isFromListView, isReadOnly, trackingPlanType } = args

  const eventNameQuery = get(args, 'query.name')
  const queryParams = eventNameQuery ? `?name=${eventNameQuery}` : ''
  const editPath = 'draft/'
  const mergePath = 'merge/'
  let parentPath = ''
  if (isFromMergeView) {
    parentPath += mergePath
  } else if (!isReadOnly) {
    parentPath += editPath
  }

  let trackingPlanTypePath = '/tracking-plans'
  let path = ''
  if (isFromListView) {
    path += 'list-'
  }
  if (isLibrary(trackingPlanType)) {
    trackingPlanTypePath = '/libraries'
    path += 'synced-tracking-plans'
  } else {
    path += 'synced-libraries'
  }
  return `/${args.workspaceSlug}/protocols${trackingPlanTypePath}/${args.trackingPlanId}/${parentPath}${path}${queryParams}`
}

export const trackingPlanConnectSourcePath = args => {
  assert(args, ['workspaceSlug', 'trackingPlanId'])
  return `/${args.workspaceSlug}/protocols/tracking-plans/connect/${args.trackingPlanId}`
}

export const trackingPlanImportLibrariesPath = args => {
  assert(args, ['workspaceSlug'])
  const urlParam = args.resourceId ? `/${args.resourceId}` : ''
  const eventNameQuery = get(args, 'query.name')
  const queryParams = eventNameQuery ? `?name=${eventNameQuery}` : ''
  return `/${
    args.workspaceSlug
  }/protocols/tracking-plans${urlParam}${queryParams}/draft/import/${args.trackingPlanType.toLowerCase()}`
}

export const sourceSchemaPathForEvent = (workspaceSlug, sourceSlug, event) => {
  const type = event.normalized.type

  const args = {
    workspaceSlug,
    sourceSlug,
    eventType: type
  }

  if (type === 'track' || type === 'page' || type === 'screen') {
    args.eventType = 'events'
  }

  return sourceSchemaPath(args)
}
